import {BaseCommand} from './base.command';
import {CommandType} from '../interfaces/command.interface';
import {MapService} from '../../../api/services/map.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata, UndoVerb} from '../undo-label.utils';

export interface SmapPropertySnapshot {
  mapId: string;
  action: 'add' | 'remove' | 'update';
  beforeMetadata: unknown[];
  afterMetadata: unknown[];
  changedField?: 'key' | 'value' | 'both' | 'row';
}

/**
 * SmapPropertyCommand handles map metadata property add/remove/update.
 */
export class SmapPropertyCommand extends BaseCommand<SmapPropertySnapshot> {
  constructor(
    private mapService: MapService,
    snapshot: SmapPropertySnapshot
  ) {
    // Map action to verb
    let verb: UndoVerb;
    switch (snapshot.action) {
      case 'add':
        verb = 'Addition';
        break;
      case 'remove':
        verb = 'Removal';
        break;
      case 'update':
        verb = 'Change';
        break;
    }

    // Determine the part label based on what changed
    let part: string;
    if (snapshot.changedField === 'key') {
      part = 'Property Key';
    } else if (snapshot.changedField === 'value') {
      part = 'Property Value';
    } else {
      part = 'Property';
    }

    const labelMetadata: UndoLabelMetadata = {
      object: 'Smap',
      verb: verb,
      part: part
    };

    let type: CommandType;
    switch (snapshot.action) {
      case 'add':
        type = 'add-property';
        break;
      case 'remove':
        type = 'remove-property';
        break;
      case 'update':
        type = 'update-property';
        break;
    }
    super(type, `Smap ${part} ${verb}`, {before: snapshot, after: snapshot}, labelMetadata);
  }

  async execute(): Promise<void> {
    const {mapId, afterMetadata} = this.snapshot.after;

    // Update on backend
    await firstValueFrom(
      this.mapService.update({
        id: mapId,
        metadata: afterMetadata
      } as any)
    );

    // Update local store by merging with existing map
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (currentMap && currentMap.id === mapId) {
      currentMap.metadata = afterMetadata as any;
      this.mapService.setCurrentSelectedMapToStore({...currentMap});
    }
  }

  async undo(): Promise<void> {
    const {mapId, beforeMetadata} = this.snapshot.before;

    // Restore previous metadata on backend
    await firstValueFrom(
      this.mapService.update({
        id: mapId,
        metadata: beforeMetadata
      } as any)
    );

    // Update local store by merging with existing map
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (currentMap && currentMap.id === mapId) {
      currentMap.metadata = beforeMetadata as any;
      this.mapService.setCurrentSelectedMapToStore({...currentMap});
    }
  }
}
